import { useEffect, useState } from "react";
import { Drawer, Table, Button, Modal, Tag, Typography, Space, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as TemplateService from "../../../services/TemplateService";
import { exportToCsv } from "../../../utils/exportToCsv";
import { formatClinicTimestamp } from "../../../utils/formatTimestamp";

/**
 * Per-template audit-log + version-history drawer for the Template Builder.
 *
 * On open (when templateId is set) it loads the audit events and the version
 * list. Audit events render newest-first as returned by the backend. Each
 * version row offers a Restore action that, on confirm, calls onRestore so the
 * page can swap the editor to the restored content.
 */

const AUDIT_CSV_COLUMNS = [
  { key: "when_display", header: "When" },
  { key: "event_type", header: "Event" },
  { key: "version_number", header: "Version" },
  { key: "actor_display", header: "Clinician" },
  { key: "detail", header: "Detail" },
];

// Resolve a clinician display label, preferring the resolved name. A missing or
// zero actor id is a system action (e.g. a seed), shown as "System"; a real id
// with no name falls back to "Clinician #<id>".
const clinicianLabel = (name, id) => {
  if (name) return name;
  if (id == null || id === 0) return "System";
  return "Clinician #" + id;
};

export default function HistoryDrawer({ open, onClose, templateId, onRestore }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [versions, setVersions] = useState([]);

  useEffect(() => {
    if (!open || !templateId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [auditRes, versionRes] = await Promise.all([
          TemplateService.getAuditEvents(templateId),
          TemplateService.listVersions(templateId),
        ]);
        if (cancelled) return;
        setEvents(auditRes?.events ?? []);
        setVersions(versionRes?.versions ?? []);
      } catch (e) {
        if (cancelled) return;
        message.error(
          e?.response?.data?.detail || e?.message || "Failed to load template history."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, templateId]);

  const handleExport = () => {
    const rows = events.map((e) => ({
      ...e,
      // exportToCsv maps by key and cannot apply a per-key fallback, so derive
      // display fields here: the clinician label and the clinic-tz timestamp.
      actor_display: clinicianLabel(e.actor_name, e.actor_id),
      when_display: formatClinicTimestamp(e.created_at),
      // A null/undefined detail JSON.stringifies to the literal "null"; export it
      // as an empty cell instead.
      detail: e.detail == null ? "" : JSON.stringify(e.detail),
    }));
    exportToCsv("template-audit.csv", rows, AUDIT_CSV_COLUMNS);
  };

  const confirmRestore = (versionNumber) => {
    Modal.confirm({
      title: `Restore version ${versionNumber}?`,
      content:
        "The template will be replaced with this version's content. You can save to keep it.",
      okText: "Restore",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await onRestore?.(versionNumber);
          message.success(`Restored version ${versionNumber}.`);
          onClose?.();
        } catch (e) {
          message.error(
            e?.response?.data?.detail || e?.message || "Failed to restore version."
          );
        }
      },
    });
  };

  const columns = [
    {
      title: "When",
      dataIndex: "created_at",
      key: "created_at",
      render: formatClinicTimestamp,
    },
    { title: "Event", dataIndex: "event_type", key: "event_type" },
    { title: "Version", dataIndex: "version_number", key: "version_number" },
    {
      title: "By",
      key: "actor",
      render: (_, row) => clinicianLabel(row.actor_name, row.actor_id),
    },
    {
      title: "Detail",
      dataIndex: "detail",
      key: "detail",
      render: (detail) =>
        // A null/undefined detail JSON.stringifies to the literal "null"; show a
        // muted hyphen placeholder instead.
        detail == null ? (
          <Typography.Text type="secondary">-</Typography.Text>
        ) : (
          <code
            style={{
              fontSize: 12,
              whiteSpace: "normal",
              overflowWrap: "anywhere",
              display: "inline-block",
              maxWidth: 200,
            }}
          >
            {JSON.stringify(detail)}
          </code>
        ),
    },
  ];

  return (
    <Drawer
      placement="right"
      width={560}
      title="Template history"
      open={open}
      onClose={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={!events.length}
          >
            Export CSV
          </Button>
        </div>

        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={events}
          columns={columns}
          pagination={false}
          locale={{ emptyText: "No history yet for this template." }}
        />

        <div>
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            Versions
          </Typography.Title>
          {versions.length === 0 ? (
            <Typography.Text type="secondary">No versions yet.</Typography.Text>
          ) : (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {versions.map((v) => (
                <div
                  key={v.version_number}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Space>
                    <span>Version {v.version_number}</span>
                    {(v.created_by_name || v.created_by != null) && (
                      <Typography.Text type="secondary">
                        by {clinicianLabel(v.created_by_name, v.created_by)}
                      </Typography.Text>
                    )}
                    {v.is_current && <Tag color="success">Current</Tag>}
                  </Space>
                  <Button
                    size="small"
                    disabled={v.is_current}
                    onClick={() => confirmRestore(v.version_number)}
                  >
                    Restore
                  </Button>
                </div>
              ))}
            </Space>
          )}
        </div>
      </Space>
    </Drawer>
  );
}
